import { PrintMarkerClip } from './print/PrintMarkerClip'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  MapMarker,
  MapView,
  tangerRasterSource,
  useMap,
  MAP_MIN_ZOOM,
  MAP_MAX_ZOOM,
  type MapViewport,
} from '@wehunt/map'
import { useTranslation, type UiTFunction } from '../i18n'
import { COLORS } from '../lib/theme'
import { Icon } from './Icon'
import { TeamMapLayers } from './TeamMapLayers'
import { PinMarkerContent } from './PinMarkerContent'
import { DEFAULT_MAP_APPEARANCE, type MapAppearance } from '../lib/mapAppearance'
import { pinTypeVisibility } from '../lib/mapPinTypes'
import { groundFramePoints, pointsToBBox, EMPTY_MAP_VIEW } from '../lib/mapSeedView'
import { usePrintPinPreview } from './print/usePrintPinPreview'
import type { PrintPinPreviewSpec } from '../lib/printPinPreview'
import type { GeoPt, MapObject, TeamMap } from '../lib/types'
import type { TeamMapViewport, TileSource } from '../lib/mapTypes'

/**
 * The MapLibre-backed read-only team map. It replaced a hand-rolled SVG/canvas
 * surface that projected lat/lon into a fixed 520x560 viewBox by itself; the job
 * is the same (frame a team's ground, its pins, and an optional interactive zoom
 * stack), backed by a real tiled map instead of that projection math.
 */
export interface TeamMapViewProps {
  /**
   * Identity contract: a new `map` object identity remounts the underlying
   * `MapView` and snaps the view back to the auto-fit bounds/`EMPTY_MAP_VIEW`
   * — the same "jump back to where we started" behavior the old canvas
   * surface had, which re-seeded its centre and zoom from an effect keyed on
   * `[map]`. This is
   * deliberate (a genuinely different team's map shouldn't keep the previous
   * map's pan/zoom), but it means a caller must pass a **stable** reference
   * across re-renders that don't actually represent a different map — a
   * fresh object built from the same underlying data on every render
   * defeats pan/zoom retention just as surely as an actual map change would.
   */
  map: TeamMap
  /** Enable pan, wheel/pinch zoom and the on-map zoom control stack. */
  interactive?: boolean
  /** The base map to draw, or null before the catalogue has arrived. */
  tileSource?: TileSource | null
  /** Overlay tile layers to composite above the base map. */
  overlayLayers?: TileSource[]
  /**
   * How the map's own features are drawn — line weights, border treatment,
   * pin and label presentation. Omitting it falls back to the defaults and,
   * unlike the defaults, suppresses pin name labels — for a surface with no
   * panel to turn them off again.
   */
  appearance?: MapAppearance
  /**
   * Called whenever the framed view changes. Pass a STABLE callback: it is
   * an effect dependency, so a fresh closure on every parent render re-runs
   * the effect each time.
   */
  onViewChange?: (view: TeamMapViewport) => void
  /**
   * Extra buttons for the on-map control stack, below zoom and recenter.
   */
  extraControls?: ReactNode
  /**
   * CLUB-676: centre the map on a point, on demand.
   *
   * Carries a `nonce` because the request is an EVENT, not a state: clicking
   * the same pin in the folder tree twice — after panning away in between —
   * has to bring you back, and a prop compared by lat/lon alone would be
   * unchanged and do nothing. The caller bumps the nonce per click.
   *
   * `zoom` is optional; omitted keeps the current zoom, which is what "show me
   * where this is" usually means once you have framed the ground yourself.
   */
  focus?: { lat: number; lon: number; zoom?: number; nonce: number; padding?: { top: number; right: number; bottom: number; left: number } } | null
  /**
   * CLUB-676: pins and lines the viewer has hidden, which are not drawn.
   *
   * A PROP rather than a pre-filtered `map`, because `map`'s identity is what
   * remounts the underlying `MapView` and snaps the view back to the auto-fit
   * bounds (see the identity contract on `map` above) — a caller rebuilding
   * the object each render to drop a pin would also reset the reader's pan and
   * zoom on every keystroke.
   *
   * Per-viewer, not per-team: this is the app's `hiddenPinIds`, one hunter
   * deciding what clutters their own map, and it never changes what anyone
   * else sees.
   */
  hiddenObjectIds?: ReadonlySet<string>
  /**
   * A ring (the print sheet's corners) outside which pins are drawn dimmed,
   * or null/omitted to draw every pin at full strength.
   *
   * The print frame's dim mask is a map LAYER, and DOM markers ride above
   * every layer — so without this the pins that will not be on the paper stay
   * as bright as the ones that will, which is the one thing the frame exists
   * to tell apart. Dimming the marker itself is the only way to reach it.
   *
   * Open or closed ring, either works: `pointInPolygon` wraps the last vertex
   * back to the first itself.
   */
  dimOutside?: GeoPt[] | null
  /**
   * The print panel's pin and label millimetres and the sheet's scale, on the
   * print surface: pins and their names are then drawn at the size the paper
   * will carry them, growing and shrinking with the frame as the map zooms,
   * instead of at the Pin size setting's fixed pixels (see `printPinPreview`).
   */
  printPinSizes?: PrintPinPreviewSpec
  /**
   * Surface-specific map content — extra layers, extra markers — rendered
   * inside the underlying `MapView` after the pins and before the zoom
   * control stack. Like every other `MapView` child, it renders only once
   * the map has finished loading.
   */
  children?: ReactNode
}

const EMPTY_OVERLAYS: TileSource[] = []

/**
 * The pin markers. A component of their own, rendered inside `MapView`, because
 * print sizing follows the live camera through `useMap()`, which resolves only
 * under the map.
 */
function TeamMapPins({
  objects,
  appearance,
  zoom,
  pinNamesVisible,
  dimOutside,
  printPinSizes,
}: {
  objects: MapObject[]
  appearance: MapAppearance
  zoom: number
  pinNamesVisible: boolean
  dimOutside?: GeoPt[] | null
  printPinSizes?: PrintPinPreviewSpec
}) {
  const printPreview = usePrintPinPreview(printPinSizes, appearance.pinBackground)
  return (
    <>
      {objects.map((o) => (
        <MapMarker key={o.id} lat={o.lat} lon={o.lon} anchor="bottom">
          {/* The wrapper carries the opacity so the pin's name label fades
              with its sprite — a bright label over a faint pin reads as a
              rendering fault, and the label is as much "this is on the
              paper" information as the sprite. It also clips the marker's
              overhang to the sheet, as the paper does. */}
          <PrintMarkerClip point={o} frame={dimOutside}>
            <PinMarkerContent
              object={o}
              appearance={appearance}
              zoom={zoom}
              pinNamesVisible={pinNamesVisible}
              printPreview={printPreview}
            />
          </PrintMarkerClip>
        </MapMarker>
      ))}
    </>
  )
}

function MapZoomButton({
  title,
  onClick,
  disabled,
  children,
}: {
  title: string
  onClick: () => void
  disabled?: boolean
  children: ReactNode
}) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 36,
        height: 36,
        borderRadius: 9999,
        border: 'none',
        background: COLORS.surface,
        color: COLORS.onSurface,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.38 : 1,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.18)',
        filter: hover && !disabled ? 'brightness(0.96)' : 'none',
        transition: 'filter .12s ease',
      }}
    >
      {children}
    </button>
  )
}

/**
 * The zoom stack, rendered inside `MapView`'s children so `useMap()` resolves
 * to the live map instance. Recenter re-applies the mount-time bounds/view —
 * the same "jump back to where we started" the old canvas surface's override
 * reset did — and stays disabled until a user gesture has actually moved the
 * map away from it.
 */
function MapControls({
  t,
  initialBounds,
  initialView,
  moved,
  onRecentered,
  extraControls,
}: {
  t: UiTFunction
  initialBounds: [number, number, number, number] | null
  initialView: MapViewport | undefined
  moved: boolean
  onRecentered: () => void
  extraControls?: ReactNode
}) {
  const map = useMap()

  const recenter = () => {
    if (!map) return
    if (initialBounds) {
      map.fitBounds(initialBounds, { animate: false, padding: 24 })
    } else if (initialView) {
      map.jumpTo({ center: [initialView.center.lon, initialView.center.lat], zoom: initialView.zoom })
    }
    onRecentered()
  }

  return (
    <div
      data-map-print="hide"
      style={{ position: 'absolute', top: 12, right: 12, display: 'flex', flexDirection: 'column', gap: 6, zIndex: 4 }}
    >
      <MapZoomButton title={t('detail.map.zoomIn')} onClick={() => map?.zoomIn()}>
        <Icon name="zoom-in" size={20} />
      </MapZoomButton>
      <MapZoomButton title={t('detail.map.zoomOut')} onClick={() => map?.zoomOut()}>
        <Icon name="zoom-out" size={20} />
      </MapZoomButton>
      <MapZoomButton title={t('detail.map.recenter')} onClick={recenter} disabled={!moved}>
        <Icon name="target" size={20} />
      </MapZoomButton>
      {extraControls}
    </div>
  )
}

/**
 * Applies a `focus` request to the live map. Rendered inside `MapView`'s
 * children, which is the only place `useMap()` resolves to the map instance.
 *
 * Keyed on the nonce ALONE: re-running whenever lat/lon are equal-but-new, or
 * on any parent re-render, would yank the map back to the last-clicked pin
 * every time the user panned away from it. `appliedNonce` is owned by
 * `TeamMapView`, which outlives this component — a `map` identity change
 * remounts everything inside `MapView`, and without a ref that survives it a
 * remount would re-apply a focus the reader has long since panned away from.
 */
function MapFocus({
  focus,
  appliedNonce,
  onFocused,
}: {
  focus?: { lat: number; lon: number; zoom?: number; nonce: number; padding?: { top: number; right: number; bottom: number; left: number } } | null
  appliedNonce: { current: number | null }
  onFocused: () => void
}) {
  const map = useMap()
  const focusNonce = focus?.nonce
  // `focus` is read as of the nonce that asked for it, through a ref rather
  // than the dependency list — as a dependency it would re-centre on every
  // parent render.
  const latest = useRef(focus)
  latest.current = focus
  useEffect(() => {
    const request = latest.current
    if (focusNonce === undefined || !request || !map) return
    if (appliedNonce.current === focusNonce) return
    appliedNonce.current = focusNonce
    map.easeTo({
      center: [request.lon, request.lat],
      ...(request.padding ? { padding: request.padding } : {}),
      zoom: Math.max(MAP_MIN_ZOOM, Math.min(MAP_MAX_ZOOM, request.zoom ?? map.getZoom())),
    })
    // The camera is no longer where the mount-time fit put it, and `MapView`
    // only reports the user's OWN gestures — so the recenter control has to be
    // told, or it stays disabled with the map pointing somewhere else.
    onFocused()
  }, [focusNonce, map, appliedNonce, onFocused])
  return null
}

export function TeamMapView({
  map,
  interactive = false,
  tileSource,
  overlayLayers,
  appearance,
  onViewChange,
  extraControls,
  focus,
  hiddenObjectIds,
  dimOutside,
  printPinSizes,
  children,
}: TeamMapViewProps) {
  const { t } = useTranslation('teams')
  const divRef = useRef<HTMLDivElement>(null)
  const [cssSize, setCssSize] = useState({ w: 600, h: 400 })

  const ap = appearance ?? DEFAULT_MAP_APPEARANCE
  const pinNamesVisible = appearance !== undefined && ap.pinNamesVisible
  // CLUB-667: unlike pinNamesVisible there is no `appearance !== undefined`
  // guard — the default hidden set is empty, so a surface with no panel is
  // already unfiltered and needs no special case.
  const pinVisible = pinTypeVisibility(ap.hiddenPinTypes)

  const tileInfo = tileSource ?? null

  const baseSource = useMemo(
    () => (tileSource ? tangerRasterSource({ ...tileSource, minZ: MAP_MIN_ZOOM, maxZ: MAP_MAX_ZOOM }) : null),
    [tileSource],
  )
  const overlays = overlayLayers ?? EMPTY_OVERLAYS
  const overlaySources = useMemo(
    () => overlays.map((o) => tangerRasterSource({ ...o, minZ: MAP_MIN_ZOOM, maxZ: MAP_MAX_ZOOM })),
    [overlays],
  )

  useEffect(() => {
    const el = divRef.current
    if (!el) return
    const obs = new ResizeObserver(([e]) => {
      const { width, height } = e.contentRect
      if (width > 0 && height > 0) setCssSize({ w: width, h: height })
    })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  // Ring points when the areas have a border, else a storage-offloaded ground's
  // [SW, NE] bbox — shared with the editor seed so both surfaces frame a
  // polygon-less ground the same way instead of dropping to the regional default.
  const groundFramePts = useMemo(
    () => groundFramePoints(map.boundaryAreas ?? []),
    [map.boundaryAreas],
  )

  const initialBounds = useMemo(() => pointsToBBox(groundFramePts), [groundFramePts])

  const initialView = initialBounds ? undefined : EMPTY_MAP_VIEW

  // Remount MapView whenever the team's map changes — MapView only reads its
  // initial bounds/view once, on mount, so this replicates the old canvas
  // surface's "snap back when the team's map changes", which it got from an
  // effect that re-seeded centre and zoom whenever the `map` prop changed.
  // The initial mount itself must not bump the epoch — that would tear down
  // and rebuild the just-created MapLibre map (a real GL context + tile
  // fetch) on every page load for no behavioral gain — so this compares
  // against the previous `map` identity rather than using a "first run"
  // boolean ref. A boolean ref misfires under StrictMode: React's dev
  // double-invoke re-runs this effect on the same mount while preserving
  // ref state, so a `firstMap.current` guard already flipped to `false` on
  // the first pass sees `false` again on the second and bumps the epoch,
  // remounting the map on every dev page load. Comparing to the previous
  // value is idempotent across that re-run (it's still the same `map`) while
  // still catching a genuine identity change.
  const [mapEpoch, setMapEpoch] = useState(0)
  const prevMapRef = useRef(map)
  useEffect(() => {
    if (prevMapRef.current !== map) {
      prevMapRef.current = map
      setMapEpoch((e) => e + 1)
      // A genuine map change snaps the view back to its fit bounds, so the
      // recenter control must go back to disabled rather than staying
      // enabled for a camera move that no longer applies to the new map.
      setMoved(false)
    }
  }, [map])

  // Null until the underlying map has reported its first real view — reporting
  // eagerly with a placeholder would forward a wrong (or merely coincidental)
  // zoom to the caller before MapView's own fit/jump has actually happened.
  const [liveView, setLiveView] = useState<MapViewport | null>(null)
  const handleMapViewChange = useCallback((v: MapViewport) => setLiveView(v), [])
  const zoom = liveView?.zoom ?? initialView?.zoom ?? MAP_MIN_ZOOM

  const [moved, setMoved] = useState(false)
  const handleUserMove = useCallback(() => setMoved(true), [])
  const handleRecentered = useCallback(() => setMoved(false), [])
  // Which focus request the live map has already been eased to — see MapFocus.
  const appliedFocusNonce = useRef<number | null>(null)

  useEffect(() => {
    if (!liveView) return
    onViewChange?.({ center: liveView.center, zoom: liveView.zoom, cssSize, tileInfo })
  }, [onViewChange, liveView, cssSize, tileInfo])

  const errorFallback = (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 16,
        color: COLORS.onSurface,
      }}
    >
      {t('detail.map.unavailable')}
    </div>
  )

  return (
    <div ref={divRef} style={{ position: 'absolute', inset: 0 }}>
      {!tileInfo && <div style={{ position: 'absolute', inset: 0, background: '#e8efdf' }} />}
      <MapView
        key={mapEpoch}
        baseSource={baseSource}
        overlaySources={overlaySources}
        initialBounds={initialBounds}
        initialView={initialView}
        interactive={interactive}
        minZoom={MAP_MIN_ZOOM}
        maxZoom={MAP_MAX_ZOOM}
        onViewChange={handleMapViewChange}
        onUserMove={handleUserMove}
        errorFallback={errorFallback}
      >
        <TeamMapLayers map={map} appearance={ap} zoom={zoom} hiddenObjectIds={hiddenObjectIds} lineLabels={pinNamesVisible} printMode={dimOutside !== undefined && dimOutside !== null} dimOutside={dimOutside} />
        {/*
          CLUB-667: the pin-type filter. `PinMarkerContent` draws the sprite and
          its name label together, so dropping the object here takes both —
          which is what "hide this pin type" has to mean.

          CLUB-676: and the viewer's own hidden pins, for the same reason. Two
          independent reasons a pin is not drawn — its TYPE is filtered off, or
          this pin was hidden from the collection — so both are checked and
          neither subsumes the other.
        */}
        <TeamMapPins
          objects={map.objects.filter((o) => pinVisible(o.type) && !hiddenObjectIds?.has(o.id))}
          appearance={ap}
          zoom={zoom}
          pinNamesVisible={pinNamesVisible}
          dimOutside={dimOutside}
          printPinSizes={printPinSizes}
        />
        <MapFocus focus={focus} appliedNonce={appliedFocusNonce} onFocused={handleUserMove} />
        {children}
        {interactive && (
          <MapControls
            t={t}
            initialBounds={initialBounds}
            initialView={initialView}
            moved={moved}
            onRecentered={handleRecentered}
            extraControls={extraControls}
          />
        )}
      </MapView>
    </div>
  )
}
